<template>
  <!-- Toast Notifications.
       Teleported to <body> with a very high z-index so they always float
       ABOVE any open overlay/bottom-sheet (otherwise a toast fired from inside
       an open sheet was trapped in a lower stacking context and hidden behind
       it). Positioned near the top so it's visible regardless of what sheet is
       open at the bottom. -->
  <Teleport to="body">
  <div class="notifications-container">
    <TransitionGroup name="notification">
      <v-alert
        v-for="notification in notifications"
        :key="notification.id"
        :type="notification.type"
        :icon="notification.icon"
        closable
        variant="elevated"
        class="notification-toast mb-2"
        @click:close="removeNotification(notification.id)"
      >
        <div class="d-flex align-center ga-2 flex-wrap">
          <span class="flex-grow-1">{{ notification.message }}</span>
          <!--
            Iteration 4 (PDF 26.05.2026, pt 8.4): optional action button on
            the toast. Used by notify.successWithAction (e.g. "View in CRM"
            deep-link after Create Job). Click runs the callback AND closes
            the toast so the operator gets a clear feedback loop.
          -->
          <v-btn
            v-if="notification.actionLabel && notification.onAction"
            size="small"
            :color="`on-${notification.type}`"
            variant="text"
            density="comfortable"
            class="notification-toast__action"
            @click="onActionClick(notification)"
          >
            {{ notification.actionLabel }}
          </v-btn>
        </div>
      </v-alert>
    </TransitionGroup>
  </div>
  </Teleport>

  <!-- Confirmation/Alert Dialog -->
  <v-dialog
    v-model="dialog.show"
    max-width="420"
    persistent
    class="global-dialog"
  >
    <v-card class="pa-2">
      <!-- Header with icon -->
      <v-card-title class="d-flex align-center pa-4 pb-2">
        <v-avatar
          :color="getDialogColor"
          size="40"
          class="mr-3"
        >
          <v-icon :color="`on-${getDialogColor}`" size="24">{{ getDialogIcon }}</v-icon>
        </v-avatar>
        <span class="text-h6">{{ dialog.title }}</span>
      </v-card-title>

      <!-- Message -->
      <v-card-text class="pa-4 pt-2 text-body-1">
        {{ dialog.message }}
        <!-- Prompt input (TICK-00032): global replacement for browser prompt() -->
        <v-text-field
          v-if="dialog.showInput"
          v-model="dialog.inputValue"
          :label="dialog.inputLabel"
          variant="outlined"
          density="compact"
          hide-details
          autofocus
          class="mt-3"
          @keyup.enter="confirmDisabled ? null : closeDialog(true)"
        />
      </v-card-text>

      <!-- Actions -->
      <v-card-actions class="pa-4 pt-0">
        <v-spacer />
        <v-btn
          v-if="dialog.showCancel"
          variant="text"
          @click="closeDialog(false)"
        >
          {{ dialog.cancelText }}
        </v-btn>
        <v-btn
          :color="getDialogColor"
          variant="flat"
          :disabled="confirmDisabled"
          @click="closeDialog(true)"
        >
          {{ dialog.confirmText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed } from 'vue'
import { useNotify } from '@/composables/useNotify'

const { notifications, dialog, removeNotification, closeDialog } = useNotify()

function onActionClick(notification) {
  if (notification?.onAction) {
    try {
      notification.onAction()
    } catch (err) {
      console.warn('[GlobalNotifications] toast action handler threw', err)
    }
  }
  removeNotification(notification.id)
}

const confirmDisabled = computed(() =>
  dialog.showInput && dialog.inputRequired && !String(dialog.inputValue || '').trim()
)

const getDialogColor = computed(() => {
  const colors = {
    success: 'success',
    error: 'error',
    warning: 'warning',
    info: 'info'
  }
  return colors[dialog.type] || 'primary'
})

const getDialogIcon = computed(() => {
  const icons = {
    success: 'mdi-check-circle',
    error: 'mdi-alert-circle',
    warning: 'mdi-alert',
    info: 'mdi-information'
  }
  return icons[dialog.type] || 'mdi-information'
})
</script>

<style scoped>
.notifications-container {
  position: fixed;
  top: 80px;
  right: 24px;
  /* Above Vuetify overlays (dialogs/bottom-sheets ~2400+) and custom
     fullscreen sheets — must never be hidden behind an open sheet. */
  z-index: 100000;
  max-width: 400px;
  width: calc(100% - 48px);
  pointer-events: none;
}

.notification-toast {
  pointer-events: auto;
  box-shadow: 0 4px 20px rgba(var(--v-theme-on-surface), 0.15);
  border-radius: 8px;
}

/* Mobile: full width near the TOP (by the header) so toasts stay visible even
   when a bottom-sheet is open. Was previously bottom-anchored, which put them
   right under the open sheet where they got covered. */
@media (max-width: 600px) {
  .notifications-container {
    top: calc(var(--app-safe-top) + 12px);
    bottom: auto;
    left: 12px;
    right: 12px;
    width: calc(100% - 24px);
    max-width: none;
  }
  
  .notification-toast {
    border-radius: 12px;
    font-size: 15px;
    padding: 4px 8px;
  }
  
  /* Mobile animations - slide down from top */
  .notification-enter-active {
    animation: slideInMobileTop 0.3s ease-out;
  }
  
  .notification-leave-active {
    animation: slideOutMobileTop 0.25s ease-in;
  }
}

/* Desktop animations */
.notification-enter-active {
  animation: slideIn 0.3s ease-out;
}

.notification-leave-active {
  animation: slideOut 0.3s ease-in;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideOut {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(100%);
  }
}

@keyframes slideInMobile {
  from {
    opacity: 0;
    transform: translateY(100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideOutMobile {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(100%);
  }
}

@keyframes slideInMobileTop {
  from {
    opacity: 0;
    transform: translateY(-100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideOutMobileTop {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-100%);
  }
}

/* Dialog styling */
.global-dialog :deep(.v-card) {
  border-radius: 12px;
}

/* Mobile dialog - more padding */
@media (max-width: 600px) {
  .global-dialog :deep(.v-card) {
    margin: 16px;
  }
}
</style>





